'use client'

import { User } from '@/domain/user'
import { ORGANIZATION_GROUP_TYPE } from '@/lib/posthog-groups'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect } from 'react'

export function PHProvider({
  children, user
}: {
  children: React.ReactNode,
  user?: User
}) {
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_POSTHOG_KEY && process.env.NEXT_PUBLIC_POSTHOG_HOST) {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
        person_profiles: 'identified_only',
        capture_pageview: false, // Disable automatic pageview capture, as we capture manually
        // Seed the identified distinct id before any capture so the first
        // $pageview is a person event (identified_only requires this to link to
        // a group) rather than anonymous. The org group itself is set in the
        // post-mount identify effect below via posthog.group(); we deliberately
        // do NOT call group() synchronously here, as that triggers a feature-flag
        // reload / provider state update before the PostHogProvider has mounted,
        // which warns under React StrictMode. (bootstrap can't carry groups.)
        ...(user ? { bootstrap: { distinctID: user.id, isIdentifiedID: true } } : {}),
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "*"
        }
      })
    }
    // Runs once on mount; intentionally captures the mount-time user (a full
    // reload on org switch re-runs this with the new org).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      const { id, ...rest } = user;
      posthog.identify(user.id, {
        ...rest
      })
      // Associate all session events with the user's organization group so
      // client-side events (pageviews, autocapture) roll up to the org, matching
      // the server-side `groups` tagging. Group properties (name, ...) are set
      // server-side via identifyOrganization; we only assert the membership here.
      posthog.group(ORGANIZATION_GROUP_TYPE, user.organizationId)
    } else {
      console.log('Resetting posthog');
      posthog.reset();
    }
  }, [user]);

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}